/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Ember from 'ember';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { filter } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import { isPresent, isBlank, isEmpty } from '@ember/utils';
import merge from 'lodash.merge';
import ApiService from 'cio/services/api';
import AnalyticsService from 'cio/services/analytics';
import EmailPreviewService from 'cio/services/email-preview';
import { Editor } from 'cio/services/editor';
import config from 'cio/config/environment';
import BroadcastTriggerModel from 'cio/models/broadcast-trigger';
import CustomerModel from 'cio/models/customer';
import LogModel from 'cio/models/log';
import ObjectModel from 'cio/models/object';
import ObjectTypeModel from 'cio/models/object-type';
import ComposerEditableEntity from 'cio/utils/composer/composer-editable-entity';
import { Platform } from 'cio/utils/push/platforms';
import { ComposerActionTypeName } from 'cio/utils/composer';
import getObjectPreviewVariables from 'cio/utils/object-preview-variables';
import getPreviewVariables from 'cio/utils/get-preview-variables';
import { EmailPreview, RequestHeader, EmailRenderErrors, HeaderError } from 'cio/utils/previews';
import { saveAs } from 'file-saver';

const PREVIEW_DEBOUNCE_DELAY = config.APP.debounceDuration;

type ViewMode = 'desktop' | 'mobile' | 'plaintext';

interface PreviewerArgs {
  editableEntity: ComposerEditableEntity;
  actionTypeName: ComposerActionTypeName;
  onPreviewChange: (preview: EmailPreview | null) => unknown;
  hasReadOnlyPermission: boolean;
}

export default class Previewer extends Component<PreviewerArgs> {
  @service api!: ApiService;
  @service analytics!: AnalyticsService;
  @service emailPreview!: EmailPreviewService;

  @tracked viewMode: ViewMode = 'desktop';
  @tracked isHeaderPanelExpanded = false;
  @tracked isShowingSendTestModal = false;
  @tracked isErrorsReviewHidden = this.isNewEmailTemplate;
  @tracked preview: EmailPreview | null = null;
  @tracked previewVariables: Record<string, unknown> = {};
  @tracked objectPreviewVariables: Record<string, ObjectModel['attributes'][]> = {};
  @tracked relatedObjects: ObjectModel[] = [];
  @tracked isShowingContextViewer = false;
  @tracked selectedCustomer?: CustomerModel;
  @tracked selectedEvent?: LogModel;
  @tracked selectedTrigger?: BroadcastTriggerModel;
  @tracked fetchedTemplatePreviewAtLeastOnce = false;

  @tracked recipient?: string;
  @tracked platform?: Platform;
  @tracked prependTest = true;

  get previewURL() {
    return this.args.editableEntity.isEmail ? 'previews/email' : '';
  }

  @action
  getPreviewData() {
    if (this.args.editableEntity.isEmail && this.args.editableEntity.template != null) {
      return this.emailPreview.generateTemplateData(
        this.args.editableEntity.template,
        this.args.editableEntity.template?.layout,
        true,
      );
    }

    return null;
  }

  get hasHeaderErrors() {
    let errors: HeaderError[] = this.preview?.errors?.headers ?? [];
    return errors.any((error) => !isEmpty(error.name) || !isEmpty(error.value));
  }

  @filter('preview.headers.@each.name', (header: RequestHeader) => !isBlank(header.name))
  validHeaders!: RequestHeader[];

  get isNewEmailTemplate() {
    return this.args.editableEntity.isEmail && isEmpty(this.args.editableEntity.template?.editor);
  }

  @computed('validHeaders', 'preview.{reply_to,bcc}')
  get headerCount() {
    let count = this.validHeaders.length || 0;

    if (isPresent(this.preview?.reply_to)) {
      count++;
    }
    if (isPresent(this.preview?.bcc)) {
      count++;
    }

    return count;
  }

  get analyticsData() {
    return {
      ...this.args.editableEntity.analyticsData,
      editor: Editor.VISUAL,
    };
  }

  @action
  toggleViewMode(viewMode: ViewMode) {
    this.viewMode = viewMode;
  }

  @action
  toggleHeaderPanel() {
    this.isHeaderPanelExpanded = !this.isHeaderPanelExpanded;
  }

  @action
  toggleSendTestModal(attrs: { recipient?: string; platform?: Platform; prependTest: boolean }) {
    this.platform = attrs.platform;
    this.recipient = attrs.recipient;
    this.prependTest = attrs.prependTest;
    this.isShowingSendTestModal = !this.isShowingSendTestModal;
  }

  @action
  toggleContextViewer() {
    this.isShowingContextViewer = !this.isShowingContextViewer;
  }

  @action
  selectEvent(event: LogModel) {
    this.selectedEvent = event;
  }

  @action
  selectTrigger(trigger: BroadcastTriggerModel) {
    this.selectedTrigger = trigger;
  }

  @action
  async selectCustomer(customer: CustomerModel) {
    await this.loadRelatedObjects.perform(customer);
    this.selectedCustomer = customer;
  }

  @action
  exportHtml() {
    if (this.args.editableEntity.template == null || !this.args.editableEntity.isEmail) {
      return;
    }
    this.trackExportHtml();

    let { id, body } = this.args.editableEntity.template;
    let blob = new Blob([body], { type: 'text/html;charset=utf-8;' });
    return saveAs(blob, `template-${id}.html`);
  }

  @action
  trackExportHtml() {
    this.analytics.track('message', 'exported_html', this.analyticsData);
  }

  fetchPreview = task({ restartable: true }, async () => {
    if (isEmpty(this.previewURL)) {
      return;
    }

    if (!Ember.testing) {
      await timeout(PREVIEW_DEBOUNCE_DELAY);
    }

    let data = this.getPreviewData();

    let { selectedCustomer, selectedEvent, selectedTrigger } = this;
    let customer = selectedCustomer ? selectedCustomer.get('attrs') : {};
    let templateVariables = this.fetchTemplateVariables.last?.value;

    if (isEmpty(templateVariables)) {
      templateVariables = await this.fetchTemplateVariables.perform();
    }

    let variables = getPreviewVariables({
      campaign: this.args.editableEntity.campaign ?? this.args.editableEntity.newsletter,
      customer,
      selectedEvent,
      selectedTrigger,
      templateVariables,
      objects: this.objectPreviewVariables,
    });

    if (data?.variables != null) {
      variables = merge({}, variables, data.variables);
      delete (data as { variables?: unknown }).variables;
    }

    if (variables.message != null && this.args.editableEntity.template != null) {
      // local template variables may be unsaved and newer than server template variables
      variables.message.subject = this.args.editableEntity.template.subject ?? variables.message.subject;
      variables.message.preheader = this.args.editableEntity.template.preheaderText ?? variables.message.preheader;
    }

    this.previewVariables = variables;

    this.preview = await this.api.post(this.previewURL, {
      data: {
        ...data,
        variables,
        lax_mode: this.args.editableEntity.isTransactionalMessage,
        editor: Editor.VISUAL,
      },
    });
    this.args.onPreviewChange(this.preview);

    // Suppress errors for newborn email templates.
    if (this.fetchedTemplatePreviewAtLeastOnce) {
      this.isErrorsReviewHidden = false;
    } else if (this.isNewEmailTemplate) {
      let errors = this.preview?.errors as EmailRenderErrors;
      errors.subject = [];
    }

    this.fetchedTemplatePreviewAtLeastOnce = true;
  });

  fetchTemplateVariables = task({ drop: true }, async () => {
    // Attribute updates don't have an associated template, so we fallback to the action's
    // variables when the template isn't available
    if (this.args.editableEntity.template) {
      let { variables } = await this.args.editableEntity.template.fetchPreviewVariables({});
      return variables;
    } else {
      let { variables } = (await this.args.editableEntity.action?.fetchPreviewVariables({})) ?? {};
      return variables;
    }
  });

  loadRelatedObjects = task(async (customer: CustomerModel) => {
    let { objects } = await customer.getRelatedObjectsPerType();

    this.relatedObjects = objects.filter((obj) => (obj.belongsTo('objectType').value() as ObjectTypeModel)?.enabled);
    this.objectPreviewVariables = getObjectPreviewVariables(this.relatedObjects, customer.id);
  });
}
